import React from 'react';
import { motion, Transition } from 'framer-motion';
import { Card as CardType } from '../../types/Card';
import './Card.css';

interface CardProps {
  card: CardType;
  isSelected?: boolean;
  isPlayable?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

// Computed once at module level — never changes after page load
const hoverEnabled =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const SUIT_SYMBOLS: Record<string, string> = {
  spades: '\u2660',
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
};

const SUIT_COLORS: Record<string, string> = {
  hearts: '#dc2626',
  diamonds: '#dc2626',
  spades: '#1e293b',
  clubs: '#1e293b',
};

const SUIT_NAMES: Record<string, string> = {
  spades: 'Spades',
  hearts: 'Hearts',
  diamonds: 'Diamonds',
  clubs: 'Clubs',
};

const RANK_NAMES: Record<string, string> = {
  A: 'Ace', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6',
  '7': '7', '8': '8', '9': '9', '10': '10', J: 'Jack', Q: 'Queen', K: 'King',
};

// Static animation variants — object identity is stable, no new objects per render
const HOVER_PLAYABLE = { scale: 1.08, y: -12, zIndex: 50 };
const HOVER_IDLE = { scale: 1, y: 0 };
const TAP_PLAYABLE = { scale: 0.95 };
const TAP_IDLE = {};
const TRANSITION: Transition = { duration: 0.4, ease: 'easeOut' };
const FACE_UP_ANIMATE = { rotateY: 0 };
const FACE_DOWN_ANIMATE = { rotateY: 180 };

const Card: React.FC<CardProps> = React.memo(({
  card,
  isSelected = false,
  isPlayable = false,
  onClick,
  className = '',
  style,
}) => {
  const suitColor = SUIT_COLORS[card.suit] ?? '#1e293b';
  const suitSymbol = SUIT_SYMBOLS[card.suit] ?? '';

  const ariaLabel = card.isFaceUp
    ? `${RANK_NAMES[card.rank] ?? card.rank} of ${SUIT_NAMES[card.suit] ?? card.suit}${isPlayable ? ', playable' : ''}${isSelected ? ', selected' : ''}`
    : 'Face-down card';

  return (
    <motion.div
      className={`card ${className} ${isSelected ? 'selected' : ''} ${isPlayable ? 'playable' : ''} ${card.isFaceUp ? 'face-up' : 'face-down'}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={ariaLabel}
      aria-pressed={isSelected || undefined}
      tabIndex={isPlayable ? 0 : undefined}
      onKeyDown={isPlayable && onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      whileHover={hoverEnabled ? (isPlayable ? HOVER_PLAYABLE : HOVER_IDLE) : undefined}
      whileTap={isPlayable ? TAP_PLAYABLE : TAP_IDLE}
      animate={card.isFaceUp ? FACE_UP_ANIMATE : FACE_DOWN_ANIMATE}
      style={style}
      transition={TRANSITION}
    >
      <div className="card-inner">
        {card.isFaceUp ? (
          <div className="card-front">
            <div className="card-corner top-left">
              <span className="rank" style={{ color: suitColor }}>{card.rank}</span>
              <span className="suit" style={{ color: suitColor }}>{suitSymbol}</span>
            </div>
            <div className="card-center">
              <div className="suit-large" style={{ color: suitColor }}>
                {suitSymbol}
              </div>
            </div>
            <div className="card-corner bottom-right">
              <span className="rank" style={{ color: suitColor }}>{card.rank}</span>
              <span className="suit" style={{ color: suitColor }}>{suitSymbol}</span>
            </div>
          </div>
        ) : (
          <div className="card-back">
            <div className="card-back-pattern" />
          </div>
        )}
      </div>
    </motion.div>
  );
});

Card.displayName = 'Card';

export default Card;
